import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  styled,
  lighten,
  useTheme,
  useMediaQuery,
  Stack,
  Grid,
  Hidden,
} from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";

// Background Images
import newBg from "../../assets/newProfessionals/homesBg.png";
import homeBgMB from "../../assets/homepage/home-mb.png";
import homeBg from "../../assets/homepage/buyBG.png";
import rentBg from "../../assets/homepage/rentBG.png";
import sellBg from "../../assets/homepage/sellBG.png";
import professionalsBg from "../../assets/homepage/professionalsBG.png";
import PropertySearch from "../PropertySearch";
import HeroServicesAd from "./HeroServicesAd";

import shades from "../../assets/newProfessionals/shades.png";
import "../newProfessionals/ProfessionalsHome.css";

// Custom Styling
const StyledTabs = styled((props) => (
  <Tabs
    {...props}
    TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
    // sx={{
    //   position: "relative",
    //   "&::after": {
    //     content: '""',
    //     position: "absolute",
    //     top: 0,
    //     right: 0,
    //     bottom: 0,
    //     width: "10px",
    //     background: {
    //       xs: "linear-gradient(to right, rgba(255, 255, 255, 0), rgba(0,0,0,0.40))",
    //       md: "transparent",
    //     },
    //     pointerEvents: "none",
    //     transition: "opacity 0.3s ease-in-out",
    //     opacity: 1,
    //     zIndex: 9999,
    //   },
    // }}
  />
))({
  "& .MuiTabs-flexContainer": {
    display: "flex",
    gap: "4px",
    backgroundColor: "transparent",
  },
  "& .MuiTabs-indicator": {
    backgroundColor: "transparent",
  },
  "& .MuiTabs-indicatorSpan": {
    width: 0,
    height: 0,
    backgroundColor: "transparent",
  },
});
const StyledTab = styled(Tab)(({ theme }) => ({
  transition: "all 0.2s",
  fontWeight: "normal",
  fontSize: "14px",
  textTransform: "none",
  borderRadius: "6px 6px 0 0",
  backgroundColor: "#CCC",
  color: "#444",
  minWidth: "unset",
  padding: "0px 16px",

  "&:hover": {
    backgroundColor: lighten("#CCC", 0.7),
  },
  "&.Mui-selected": {
    backgroundColor: theme.palette.paper.main,
    color: theme.palette.text.main,
  },
}));

export default function HeroSection() {
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.up("sm"));

  const [activeTab, setActiveTab] = useState(0);
  const [loadedImages, setLoadedImages] = useState();

  const searchTabs = useMemo(() => {
    return [
      {
        key: "buy",
        label: "Buy",
        title: `Find the right home \n at the right price`,
        bg: newBg,
        placeholder: "City, Address, School, Agent, ZIP",
      },
      {
        key: "rent",
        label: "Rent",
        title: "Local rentals \n at your fingertips",
        bg: rentBg,
        placeholder: "City, Address, School, Agent, ZIP",
      },
      {
        key: "sell",
        label: "Sell",
        title: "Sell with the industry's \n most experienced agents",
        bg: sellBg,
        placeholder: "Enter address, city or zipcode",
      },
      {
        key: "professionals",
        label: "Industry Pros",
        title: "Find industry professionals \n in your neighborhood",
        bg: professionalsBg,
        placeholder: "Enter city name", // to find experts
        serviesList: [
          "Industry Professionals",
          "Need a Pro",
          "Attorneys",
          "Contractors",
          "Stagers",
          "Electricians",
          "Plumbers",
          "Roofers",
        ],
      },
      {
        key: "home-estimate",
        label: "My Home Estimate",
        title: "Get home estimate \n from expert agents",
        bg: homeBg,
        placeholder: "Enter city name to get report",
      },
      {
        key: "agent-finder",
        label: "Agent Finder",
        title: "Find top BPO \n Agents in your area",
        bg: homeBg,
        placeholder: "Enter city name", // to find agents
        serviesList: [
          "Agent Finder",
          "Our Agents Provide;",
          "BPO Reports",
          "Proforma Reports",
          "Property Evaluations",
          "Property Tours",
          "Buyer Representation",
          "Seller Representation",
        ],
      },
    ];
  }, []);

  const searchType = useMemo(() => {
    return searchTabs[activeTab]?.key;
  }, [activeTab, searchTabs]);

  const handleTabChange = (evt, val) => {
    setActiveTab(val);
  };

  const renderWithLineBreaks = (text = "") => {
    const lines = text.split("\n");
    return lines.map((line, index) => (
      <Fragment key={index}>
        {line}
        {index < lines.length - 1 && (
          <br style={{ display: sm ? "block" : "none" }} />
        )}
      </Fragment>
    ));
  };

  // Preload images
  useEffect(() => {
    searchTabs.forEach((tab) => {
      const img = new Image();
      img.onload = () => {
        setLoadedImages((prev) => ({ ...prev, [tab.bg]: true }));
      };
      img.src = tab.bg;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack
      position="relative"
      justifyContent="center"
      height={{ xs: "600px", md: "72vh" }}
    >
      {/* <AnimatePresence mode="popLayout">
      </AnimatePresence> */}

      <motion.div
        key={activeTab}
        initial={{
          opacity: 0.9,
          // x: loadedImages ? "10%" : 0,
        }}
        animate={{
          opacity: 1,
          // x: 0
        }}
        exit={{
          opacity: 0.9,
          // x: "-10%"
        }}
        transition={{ duration: 0.5 }}
        style={{
          position: "absolute",
          height: "100%",
          width: "100%",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: "100%",
            // backgroundImage: `url(${searchTabs[activeTab]?.bg ?? homeBg})`,
            backgroundColor: "background.default",
            backgroundImage: { xs: `url(${homeBgMB})`, md: `url(${newBg})` },
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            "&::before": {
              content: '""',
              position: "absolute",
              backgroundColor: {
                xs: "rgba(0,0,0,0.50)",
                md: "transparent",
              },
              zIndex: 0,
              left: 0,
              top: 0,
              right: 0,
              bottom: 0,
            },
          }}
        />
      </motion.div>

      <Container
        maxWidth="xl"
        sx={{
          position: "unset",
          px: 2.5,
        }}
      >
        <Grid container>
          <Grid item xs={12} md={8} display="flex" alignItems="center">
            <Box
              position="relative"
              sx={{
                width: "100%",
              }}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{
                    opacity: 0,
                    x: loadedImages ? 20 : 0,
                  }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  <Typography
                    variant="h2"
                    component="h1"
                    color="white"
                    fontWeight="bold"
                    sx={{
                      mb: { xs: 3.0, md: 6.25 },
                      fontSize: { xs: "1.875rem", sm: "2.5rem", md: "3.5rem" },
                      lineHeight: 1.2,
                    }}
                  >
                    {searchTabs[activeTab]?.title ? (
                      renderWithLineBreaks(searchTabs[activeTab]?.title)
                    ) : (
                      <>
                        Find the right home
                        <br style={{ display: sm ? "block" : "none" }} />
                        at the right price
                      </>
                    )}
                  </Typography>
                </motion.div>
              </AnimatePresence>

              <StyledTabs
                variant="scrollable"
                scrollButtons="auto"
                aria-label="BPO Homes Search Tabs"
                value={activeTab}
                onChange={handleTabChange}
              >
                {searchTabs.map(({ label, key }) => (
                  <StyledTab key={key} label={label} />
                ))}
              </StyledTabs>

              <Box maxWidth={{ xs: "unset", md: "604px" }}>
                <PropertySearch
                  hero
                  searchType={searchType}
                  placeholder={searchTabs[activeTab]?.placeholder}
                  width="100%"
                />
              </Box>
            </Box>
          </Grid>

          <Hidden mdDown>
            {searchTabs[activeTab]?.serviesList ? (
              <Grid item md={4} sx={{ height: { xs: "600px", md: "72vh" } }}>
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: "33.333333%",
                    height: { xs: "600px", md: "72vh" },
                  }}
                >
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeTab}
                      initial={{ opacity: 0, x: "100%" }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: "100%" }}
                      transition={{ duration: 0.5 }}
                      style={{
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          backgroundImage: `url(${shades})`,
                          backgroundSize: "cover",
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "100% 100%",
                          width: "100%",
                          height: "100%",
                        }}
                      >
                        <HeroServicesAd
                          services={searchTabs[activeTab]?.serviesList}
                        />
                      </Box>
                    </motion.div>
                  </AnimatePresence>
                </Box>
              </Grid>
            ) : null}
          </Hidden>
        </Grid>
      </Container>
    </Stack>
  );
}
